import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import XButton from 'components/inputs/x-button';
import { SystemOptionType } from 'data/types';
import { useComparisonSelection } from 'hooks/use-comparison-selection';
import { useMemo } from 'react';
import {
  ductedDuplicateNameIndexes,
  nameWithDuplicateIndex
} from 'utils/duplicate-system-names';
import { formatPrice } from 'utils/helpers';
import { resolveComparisonSystemName } from './comparison-modal-helpers';

type ComparisonTabsProps = Readonly<{
  onSelectSystem: (system: SystemOptionType) => void;
  onOpenComparisonModal: () => void;
}>;

type ComparisonTabProps = Readonly<{
  system: SystemOptionType;
  index: number;
  nameIndex: number | undefined;
  onSelect: () => void;
  onRemove: () => void;
}>;

function ComparisonTab({
  system,
  index,
  nameIndex,
  onSelect,
  onRemove
}: ComparisonTabProps) {
  const displayName = nameWithDuplicateIndex(
    resolveComparisonSystemName(system, index),
    nameIndex
  );

  return (
    <div className='flex min-w-0 items-center gap-2 rounded-md border border-gray-200 bg-white py-1.5 pl-3 pr-2 shadow-sm hover:border-gray-300'>
      <button
        type='button'
        onClick={onSelect}
        title={`${displayName} — ${system.configuration.name}`}
        className='flex min-w-0 grow items-baseline gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
      >
        <span className='truncate text-sm font-semibold text-gray-900'>
          {displayName}
        </span>
        <span className='shrink-0 text-xs font-semibold text-green-600'>
          {formatPrice(system.price)}
        </span>
      </button>
      <XButton
        onClick={onRemove}
        size='sm'
        color='gray'
        tooltip='Remove from comparison'
      />
    </div>
  );
}

export default function ComparisonTabs({
  onSelectSystem,
  onOpenComparisonModal
}: ComparisonTabsProps) {
  const systemsToCompare = useAppSelector(
    (state) => state.systems.systemsToCompare
  );
  const { removeSystem } = useComparisonSelection();
  const nameIndexes = useMemo(
    () => ductedDuplicateNameIndexes(systemsToCompare),
    [systemsToCompare]
  );

  if (systemsToCompare.length === 0) {
    return (
      <div className='shrink-0 rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-2.5 text-center text-sm text-gray-500'>
        No systems selected for comparison yet. Add one below to compare them
        side by side.
      </div>
    );
  }

  const canCompare = systemsToCompare.length > 1;

  return (
    <div className='flex shrink-0 flex-wrap items-center gap-2'>
      {systemsToCompare.map((system, index) => (
        <ComparisonTab
          key={system.id}
          system={system}
          index={index}
          nameIndex={nameIndexes.get(system.id)}
          onSelect={() => onSelectSystem(system)}
          onRemove={() => removeSystem(system.id)}
        />
      ))}
      <Button
        color='secondary'
        text='Compare Side by Side'
        icon={ArrowsRightLeftIcon}
        iconSize='sm'
        disabled={!canCompare}
        tooltip={
          canCompare ? undefined : 'Add another system to compare side by side'
        }
        onClick={onOpenComparisonModal}
      />
    </div>
  );
}
